/**
 * Audio Context
 * React context for centralized audio state management
 * Unified control for music, narration, voice commands, and game sounds
 */

/* eslint-disable react-refresh/only-export-components */

import React, { createContext, useState, useEffect, useCallback, useRef } from 'react';
import type { AudioState, NarrationOptions, MusicTrack } from './types';

// Sound settings for game effects (unified from old SoundSettingsContext)
export interface SoundSettings {
  enabled: boolean;
  volume: number;
}

// Background music settings (separate from game effects)
export interface MusicSettings {
  enabled: boolean;
  volume: number;
}

// Voice/narration settings (separate from music and game effects)
export interface NarrationSettings {
  enabled: boolean;
}

export interface VoiceSettings {
  enabled: boolean;
}

const SOUND_STORAGE_KEY = 'game-sound-settings';
const MUSIC_STORAGE_KEY = 'game-music-settings';
const NARRATION_STORAGE_KEY = 'game-narration-settings';
const VOICE_STORAGE_KEY = 'game-voice-settings';

const getStoredSoundSettings = (): SoundSettings => {
  try {
    const stored = localStorage.getItem(SOUND_STORAGE_KEY);
    if (stored) {
      return JSON.parse(stored);
    }
  } catch {
    // Ignore errors
  }
  return { enabled: true, volume: 0.7 };
};

const getStoredMusicSettings = (): MusicSettings => {
  try {
    const stored = localStorage.getItem(MUSIC_STORAGE_KEY);
    if (stored) {
      const parsed = JSON.parse(stored);
      return {
        enabled: Boolean(parsed?.enabled),
        volume: Math.max(0, Math.min(1, Number(parsed?.volume ?? 0.5))),
      };
    }
  } catch {
    // Ignore errors
  }

  // Background music is OFF by default.
  return { enabled: false, volume: 0.5 };
};

const getStoredNarrationSettings = (): NarrationSettings => {
  try {
    const stored = localStorage.getItem(NARRATION_STORAGE_KEY);
    if (stored) {
      const parsed = JSON.parse(stored);
      return { enabled: Boolean(parsed?.enabled) };
    }
  } catch {
    // Ignore errors
  }

  // Narration/instructions can remain on by default.
  return { enabled: true };
};

const getStoredVoiceSettings = (): VoiceSettings => {
  try {
    const stored = localStorage.getItem(VOICE_STORAGE_KEY);
    if (stored) {
      const parsed = JSON.parse(stored);
      return { enabled: Boolean(parsed?.enabled) };
    }
  } catch {
    // Ignore errors
  }

  // Voice commands are OFF by default (explicit opt-in required).
  return { enabled: false };
};

// Default audio state for when managers aren't loaded yet
const defaultAudioState: AudioState = {
  audioEnabled: true,
  musicPlaying: false,
  musicMuted: false,
  musicVolume: 0.5,
  narrationActive: false,
  narrationQueue: [],
  voiceEnabled: false,
  voiceListening: false,
  voicePermission: 'unknown',
};

export interface AudioContextType {
  // State
  state: AudioState;

  // Sound settings (game effects) - replaces SoundSettingsContext
  soundSettings: SoundSettings;
  setSoundEnabled: (enabled: boolean) => void;
  setSoundVolume: (volume: number) => void;
  toggleSoundEnabled: () => void;

  // Music settings (background music)
  musicSettings: MusicSettings;
  setMusicEnabled: (enabled: boolean) => void;
  setMusicVolume: (volume: number) => void;
  toggleMusicEnabled: () => void;

  // Narration settings (voice/instructions)
  narrationSettings: NarrationSettings;
  setNarrationEnabled: (enabled: boolean) => void;
  toggleNarrationEnabled: () => void;

  // Music control
  playMusic: (trackId: string) => void;
  pauseMusic: () => void;
  resumeMusic: () => void;
  toggleMusicMute: () => void;
  registerMusicTrack: (track: MusicTrack) => void;

  // Narration control
  speak: (options: NarrationOptions) => Promise<void>;
  cancelNarration: () => void;
  toggleNarrationMute: () => void;

  // Voice commands
  startVoiceCommands: () => void;
  stopVoiceCommands: () => void;
  registerVoiceCommand: (command: string, callback: (text: string, confidence: number) => void) => () => void;

  // Global
  setAudioEnabled: (enabled: boolean) => void;
  setVoiceEnabled: (enabled: boolean) => void;
}

export const AudioContext = createContext<AudioContextType | null>(null);

export interface AudioProviderProps {
  children: React.ReactNode;
}

// Lazy-loaded manager references
interface AudioManagers {
  audioManager: typeof import('./audioManager').audioManager;
  musicManager: typeof import('./musicManager').musicManager;
  narrationManager: typeof import('./narrationManager').narrationManager;
  voiceCommandManager: typeof import('./voiceCommandManager').voiceCommandManager;
}

/**
 * Audio Provider
 * Wraps app with centralized audio state management
 * Now includes sound settings for game effects
 * Uses lazy-loading to avoid circular dependency issues
 */
export const AudioProvider: React.FC<AudioProviderProps> = ({ children }) => {
  const [state, setState] = useState<AudioState>(defaultAudioState);
  const [soundSettings, setSoundSettings] = useState<SoundSettings>(getStoredSoundSettings);
  const [musicSettings, setMusicSettings] = useState<MusicSettings>(getStoredMusicSettings);
  const [narrationSettings, setNarrationSettings] = useState<NarrationSettings>(getStoredNarrationSettings);
  const [voiceSettings, setVoiceSettings] = useState<VoiceSettings>(getStoredVoiceSettings);
  const [managersLoaded, setManagersLoaded] = useState(false);
  const managersRef = useRef<AudioManagers | null>(null);
  const lastChangedAudioSettingRef = useRef<'sound' | 'narration' | null>(null);

  // Lazy-load audio managers after component mounts
  useEffect(() => {
    let cancelled = false;

    const loadManagers = async () => {
      try {
        const [audioMod, musicMod, narrationMod, voiceMod] = await Promise.all([
          import('./audioManager'),
          import('./musicManager'),
          import('./narrationManager'),
          import('./voiceCommandManager'),
        ]);

        if (cancelled) return;

        managersRef.current = {
          audioManager: audioMod.audioManager,
          musicManager: musicMod.musicManager,
          narrationManager: narrationMod.narrationManager,
          voiceCommandManager: voiceMod.voiceCommandManager,
        };

        // Initialize and get initial state
        audioMod.audioManager.init();
        setState(audioMod.audioManager.getState());
        setManagersLoaded(true);

        // Subscribe to state changes
        const unsubscribe = audioMod.audioManager.subscribe((newState) => {
          setState(newState);
        });

        return unsubscribe;
      } catch (error) {
        console.error('Failed to load audio managers:', error);
      }
    };

    const unsubscribePromise = loadManagers();

    return () => {
      cancelled = true;
      unsubscribePromise?.then((unsubscribe) => unsubscribe?.());
      if (managersRef.current) {
        managersRef.current.audioManager.destroy();
      }
    };
  }, []);

  // Persist sound settings to localStorage
  useEffect(() => {
    try {
      localStorage.setItem(SOUND_STORAGE_KEY, JSON.stringify(soundSettings));
    } catch {
      // Ignore storage errors (private mode, disabled storage, quota exceeded)
    }
  }, [soundSettings]);

  // Persist music settings to localStorage
  useEffect(() => {
    try {
      localStorage.setItem(MUSIC_STORAGE_KEY, JSON.stringify(musicSettings));
    } catch {
      // Ignore storage errors
    }
  }, [musicSettings]);

  // Persist narration settings to localStorage
  useEffect(() => {
    try {
      localStorage.setItem(NARRATION_STORAGE_KEY, JSON.stringify(narrationSettings));
    } catch {
      // Ignore storage errors
    }
  }, [narrationSettings]);

  // Persist voice settings to localStorage
  useEffect(() => {
    try {
      localStorage.setItem(VOICE_STORAGE_KEY, JSON.stringify(voiceSettings));
    } catch {
      // Ignore storage errors
    }
  }, [voiceSettings]);

  // Sound settings controls - also sync narration for master mute
  const setSoundEnabled = useCallback((enabled: boolean) => {
    lastChangedAudioSettingRef.current = 'sound';
    setSoundSettings(prev => ({ ...prev, enabled }));
    // When toggling game sounds off/on, also toggle narration for consistent mute behavior
    setNarrationSettings(prev => ({ ...prev, enabled }));
  }, []);

  const setSoundVolume = useCallback((volume: number) => {
    setSoundSettings(prev => ({ ...prev, volume: Math.max(0, Math.min(1, volume)) }));
  }, []);

  const toggleSoundEnabled = useCallback(() => {
    lastChangedAudioSettingRef.current = 'sound';
    setSoundSettings(prev => {
      const newEnabled = !prev.enabled;
      // When toggling game sounds, also toggle narration in sync
      setNarrationSettings(current => ({ ...current, enabled: newEnabled }));
      return { ...prev, enabled: newEnabled };
    });
  }, []);

  // Helper to safely access managers
  const getManagers = () => managersRef.current;

  // Keep managers aligned with persisted settings after load.
  useEffect(() => {
    if (!managersLoaded) return;
    const managers = getManagers();
    if (!managers) return;

    // Apply initial music settings (do not auto-play when disabled).
    managers.musicManager.setVolume(musicSettings.volume);
    if (!musicSettings.enabled) {
      managers.musicManager.pause();
      managers.musicManager.setMuted(true);
    }

    // Apply initial narration setting.
    managers.narrationManager.setEnabled(narrationSettings.enabled);

    // Apply initial voice opt-in setting.
    managers.audioManager.setVoiceEnabled(voiceSettings.enabled);
  }, [managersLoaded, musicSettings.enabled, musicSettings.volume, narrationSettings.enabled, voiceSettings.enabled]);

  // Respect explicit audio consent modal (global event) so user choices apply instantly
  useEffect(() => {
    const onConsent = (e: any) => {
      try {
        const granted = !!e?.detail?.granted;
        // If user denies, disable narration and voice commands
        if (!granted) {
          setNarrationSettings({ enabled: false });
          setVoiceSettings({ enabled: false });
          const managers = getManagers();
          managers?.narrationManager.setEnabled(false);
          managers?.audioManager.setVoiceEnabled(false);
        } else {
          setNarrationSettings({ enabled: true });
          setVoiceSettings({ enabled: true });
          const managers = getManagers();
          managers?.narrationManager.setEnabled(true);
          managers?.audioManager.setVoiceEnabled(true);
        }
      } catch (e) {
        // ignore
      }
    };
    window.addEventListener('cges:audio-consent', onConsent as EventListener);
    return () => window.removeEventListener('cges:audio-consent', onConsent as EventListener);
  }, []);

  // Apply music volume changes
  useEffect(() => {
    const managers = getManagers();
    managers?.musicManager.setVolume(musicSettings.volume);
  }, [musicSettings.volume]);

  // Apply narration enabled changes
  useEffect(() => {
    const managers = getManagers();
    managers?.narrationManager.setEnabled(narrationSettings.enabled);
  }, [narrationSettings.enabled]);

  // Keep narration in sync with game sound settings for consistent mute behavior
  // When user mutes game sounds via quick toggle, narration mutes too
  // But allow manual independent control via the settings popover
  useEffect(() => {
    // Only enforce sync if the user just toggled sound settings (not if they manually adjusted narration)
    const lastChanged = lastChangedAudioSettingRef.current;
    if (lastChanged === 'sound' && !soundSettings.enabled && narrationSettings.enabled) {
      // Sound was just disabled but narration is still on - disable narration too
      setNarrationSettings(prev => ({ ...prev, enabled: false }));
      lastChangedAudioSettingRef.current = null;
    }
  }, [soundSettings.enabled, narrationSettings.enabled]);

  const setMusicEnabled = useCallback((enabled: boolean) => {
    setMusicSettings(prev => ({ ...prev, enabled }));

    const managers = getManagers();
    if (!managers) return;

    if (!enabled) {
      managers.musicManager.pause();
      managers.musicManager.setMuted(true);
      return;
    }

    managers.musicManager.init();
    managers.musicManager.setMuted(false);
    const current = managers.musicManager.getCurrentTrack();
    if (current) {
      managers.musicManager.play(current);
    } else {
      managers.musicManager.resume();
    }
  }, []);

  const setMusicVolume = useCallback((volume: number) => {
    const next = Math.max(0, Math.min(1, volume));
    setMusicSettings(prev => ({ ...prev, volume: next }));
    const managers = getManagers();
    managers?.musicManager.setVolume(next);
  }, []);

  const toggleMusicEnabled = useCallback(() => {
    setMusicEnabled(!musicSettings.enabled);
  }, [musicSettings.enabled, setMusicEnabled]);

  const setNarrationEnabled = useCallback((enabled: boolean) => {
    setNarrationSettings({ enabled });
    const managers = getManagers();
    managers?.narrationManager.setEnabled(enabled);
  }, []);

  const setVoiceEnabled = useCallback((enabled: boolean) => {
    setVoiceSettings({ enabled });
    const managers = getManagers();
    managers?.audioManager.setVoiceEnabled(enabled);
  }, []);

  const toggleNarrationEnabled = useCallback(() => {
    setNarrationEnabled(!narrationSettings.enabled);
  }, [narrationSettings.enabled, setNarrationEnabled]);

  const value: AudioContextType = {
    state,

    // Sound settings (game effects)
    soundSettings,
    setSoundEnabled,
    setSoundVolume,
    toggleSoundEnabled,

    // Music settings
    musicSettings,
    setMusicEnabled,
    setMusicVolume,
    toggleMusicEnabled,

    // Narration settings
    narrationSettings,
    setNarrationEnabled,
    toggleNarrationEnabled,

    // Music control
    playMusic: (trackId: string) => getManagers()?.musicManager.play(trackId),
    pauseMusic: () => getManagers()?.musicManager.pause(),
    resumeMusic: () => getManagers()?.musicManager.resume(),
    toggleMusicMute: () => getManagers()?.musicManager.toggleMute(),
    registerMusicTrack: (track: MusicTrack) => getManagers()?.musicManager.registerTrack(track),

    // Narration control
    speak: async (options: NarrationOptions) => {
      await getManagers()?.narrationManager.speak(options);
    },
    cancelNarration: () => getManagers()?.narrationManager.cancel(),
    toggleNarrationMute: () => getManagers()?.narrationManager.toggleMute(),

    // Voice commands
    startVoiceCommands: () => getManagers()?.voiceCommandManager.start(),
    stopVoiceCommands: () => getManagers()?.voiceCommandManager.stop(),
    registerVoiceCommand: (command: string, callback: (text: string, confidence: number) => void) => {
      const managers = getManagers();
      if (managers) {
        return managers.voiceCommandManager.on(command, callback);
      }
      return () => {}; // no-op unsubscribe if managers not loaded
    },

    // Global
    setAudioEnabled: (enabled: boolean) => getManagers()?.audioManager.setEnabled(enabled),
    setVoiceEnabled,
  };

  return <AudioContext.Provider value={value}>{children}</AudioContext.Provider>;
};

/**
 * Legacy compatibility: useSoundSettings hook
 * This provides the same API as the old SoundSettingsContext for easy migration
 */
export const useSoundSettings = () => {
  const context = React.useContext(AudioContext);
  if (!context) {
    throw new Error('useSoundSettings must be used within an AudioProvider');
  }
  return {
    settings: context.soundSettings,
    setEnabled: context.setSoundEnabled,
    setVolume: context.setSoundVolume,
    toggleEnabled: context.toggleSoundEnabled,
  };
};
